import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPinOff, Globe2, Store, Tag, ArrowUpRight } from "lucide-react";
import { isMhqUser } from "@/lib/permissions";


function StatLink({ label, value, hint, testId, icon: Icon, onClick, mono = true }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group flex w-full flex-col items-start gap-3 rounded-md border border-border bg-card p-5 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
          <span className="data-label">{label}</span>
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>
      <div className={mono ? "font-mono text-4xl font-semibold tabular-nums" : "text-4xl font-semibold"}>{value}</div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </button>
  );
}

export default function Home() {
  const { user, impersonating } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const isMhq = isMhqUser(user);
  const showDealerHome = !isMhq || impersonating;

  useEffect(() => {
    if (showDealerHome) {
      const dealerId = impersonating?.id || user?.dealer_id;
      if (!dealerId) return;
      client.get(`/dealers/${dealerId}/summary`).then((r) => setStats({ dealer: r.data })).catch((e) => setError(e));
      return;
    }
    client.get(`/dashboard/stats`).then((r) => setStats(r.data)).catch((e) => setError(e));
  }, [isMhq, impersonating, user, showDealerHome]);

  if (showDealerHome) {
    const dealer = stats?.dealer?.dealer;
    return (
      <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="dealer-home">
        <div className="mb-8">
          <p className="data-label mb-2">
            {t("nav.home")} · {dealer ? t("nav.myDealership") : "…"}
          </p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            {dealer ? t("home.welcome", dealer.name) : "…"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("home.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card
            data-testid="tile-my-dealership"
            className="cursor-pointer p-5 transition-colors hover:border-primary/40 hover:bg-muted/40"
            onClick={() => navigate("/my-dealership")}
          >
            <Store className="mb-3 h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-lg font-semibold">{t("nav.myDealership")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("home.myDealershipHint")}
            </p>
          </Card>
          <Card
            data-testid="tile-legal-pages"
            className="cursor-pointer p-5 transition-colors hover:border-primary/40 hover:bg-muted/40"
            onClick={() => navigate("/legal-pages")}
          >
            <Tag className="mb-3 h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-lg font-semibold">{t("nav.legalPages")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("home.legalPagesHint")}
            </p>
          </Card>
          {dealer && (
            <Card className="p-5" data-testid="tile-code">
              <p className="data-label mb-1">{t("home.dealerCode")}</p>
              <p className="font-mono text-xl font-semibold tabular-nums">{dealer.global_id}</p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {t("home.companyId", dealer.company_id)}
              </p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // MHQ dashboard
  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="mhq-home">
      <div className="mb-8">
        <p className="data-label mb-2">{t("nav.home")} · {t("dashboard.title").toUpperCase()}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("dashboard.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
      </div>

      {!stats && !error && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      )}

      {stats && (
        <>
          {/* Service type card — group of counts + total */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <Card className="p-6 lg:col-span-8" data-testid="dashboard-service-types">
              <div className="mb-5 flex items-center justify-between">
                <span className="data-label">{t("dashboard.serviceTypes")}</span>
                <span className="text-xs text-muted-foreground">
                  {t("dashboard.total")}: <b className="font-mono tabular-nums text-foreground">{stats.total_dealers}</b>
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {Object.entries(stats.service_types).map(([type, count]) => (
                  <StatLink
                    key={type}
                    label={type}
                    value={count}
                    hint={t("home.offeringHint", type)}
                    testId={`stat-service-${type}`}
                    icon={Tag}
                    onClick={() => navigate(`/dealers?service_type=${type}`)}
                  />
                ))}
              </div>
            </Card>

            <div className="grid grid-cols-1 gap-4 lg:col-span-4">
              <StatLink
                label={t("dashboard.missingCoords")}
                value={stats.missing_coords}
                hint={t("dashboard.missingCoordsSub")}
                testId="stat-missing-coords"
                icon={MapPinOff}
                onClick={() => navigate(`/dealers?missing_coords=1`)}
              />
              <StatLink
                label={t("dashboard.sitesCount")}
                value={stats.sites_count}
                hint={t("dashboard.sitesSub")}
                testId="stat-sites"
                icon={Globe2}
                onClick={() => navigate(`/dealers`)}
              />
            </div>
          </div>
        </>
      )}

      {error && (
        <p data-testid="dashboard-error" className="text-sm text-destructive">
          {t("home.statsError")}
        </p>
      )}
    </div>
  );
}
